// @flow
import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs'
import Header from '../Main/Header/Header.js'
import { Notes } from 'components/Notes'
import { S3FilePreview } from 'components/S3FilePreview'
import AttachmentForm from '../Main/AttachmentForm'
import AccountInfo from './AccountInfo'
import { unsetModal, setModal } from 'lib/modal/actions'
import { setBanner, setLoad, setUnload } from 'lib/notification/actions'
import { httpDelete } from 'lib/http'
import SubaccountEditForm from './SubaccountEditForm'
import LogInfo from '../../../../../Distributions/components/Footer/LogInfo.js'

import styles from '../Main/styles.css'

type Props = {
  subaccount?: Object,
  accountSummary?: Object,
  accountNumber?: string,
  subnumber?: string,
  notes?: Array<Object>,
  dispatch?: Function,
  attachments?: Array<Object>,
  onSuccess?: Function,
}

const renderBackLink = (number, subnumber) => {
  const subaccountUrl = `/accounts/${number}/subaccounts/${subnumber}`
  return <Link to={subaccountUrl}>Back to Subaccount</Link>
}

const openAttachmentModal = (number, subnumber, dispatch, onSuccess) => {
  const modalForm = <AttachmentForm number={number} subnumber={subnumber} onSuccess={onSuccess} />
  dispatch(setModal(modalForm))
}

const deleteFile = (dispatch, onSuccess, number, subnumber, fileId) => {
  return (
    <section>
      <div className="section">
        <h4>Are you sure you want to delete this file?</h4>
      </div>
      <div className={styles.buttons}>
        <button className={styles.removeBtn} onClick={() => handleDelete(dispatch, onSuccess, number, subnumber, fileId)}>
          Delete File
        </button>
        <span className={styles.spacer} />
        <button className={styles.cancelBtn} onClick={() => dispatch(unsetModal())}>
          Cancel
        </button>
      </div>
    </section>
  )
}

const handleDelete = (dispatch, onSuccess, number, subnumber, fileId) => {
  dispatch(setLoad())
  const url = `/admin/accounts/${number}/subaccounts/${subnumber}/attachments/${fileId}`
  return httpDelete(url)
    .then(() => {
      dispatch(onSuccess)
      dispatch(setBanner('Document successfully deleted'))
    })
    .catch(() => {
      dispatch(setBanner('File deletion failed', 'danger'))
    })
    .then(() => {
      dispatch(unsetModal())
      dispatch(setUnload())
    })
}

const showLink = (file) => <S3FilePreview file_key={file.key} label={file.name} />

const showAttachments = ({ attachments, accountNumber, subnumber, dispatch, onSuccess }) => {
  if (!Array.isArray(attachments)) return null
  let docs
  if (attachments.length) {
    docs = attachments.map((attachment, idx) => {
      const { file_id } = attachment
      const deleteHandler = () => {
        dispatch(setModal(deleteFile(dispatch, onSuccess, accountNumber, subnumber, file_id)))
      }
      return (
        <div className={styles.otherDocsRow} key={idx}>
          {showLink(attachment)}
          <div className={styles.remove}>
            <a title="Delete File" onClick={deleteHandler} />
          </div>
        </div>
      )
    })
  } else {
    docs = <div className={styles.noDocResults}>No attachments</div>
  }
  return (
    <section className={styles.cardMarginBot}>
      <div className={styles.docsWrapper}>{docs}</div>
    </section>
  )
}

export const Template = ({ subaccount, accountSummary, accountNumber, subnumber, notes, dispatch, attachments, onSuccess }: Props) => {
  const navigate = useNavigate()

  const handleCancel = () => {
    navigate(`/accounts/${accountNumber}/subaccounts/${subnumber}`)
  }

  if (!subaccount) {
    return <div>Loading subaccount data...</div>
  }
  const events = subaccount?.events || []

  return (
    <div>
      <Breadcrumbs crumbs={[renderBackLink(accountNumber, subnumber)]} />
      <section className={styles.contentContainer}>
        <section className={styles.twoThird}>
          <Header
            balance={subaccount.balance}
            beneficiary_name={subaccount.beneficiary_name}
            number={accountNumber || ''}
            subaccount_type={subaccount.subaccount_type}
            subnumber={subnumber || ''}
          />
          <section>
            <SubaccountEditForm
              subaccount={subaccount}
              accountNumber={accountNumber}
              subnumber={subnumber}
              dispatch={dispatch}
              onSuccess={onSuccess}
              onCancel={handleCancel}
            />
          </section>
          <section className={styles.logSection}>
            <LogInfo
              useContainer={false}
              events={events}
              headers={[
                { key: 'createdAt', value: 'When', className: styles.logTableHeader },
                { key: 'createdByUser', value: 'Who', className: styles.logTableHeader },
                { key: 'description', value: 'Action', className: styles.logTableHeader },
              ]}
            />
          </section>
        </section>
        <div className={styles.oneThird}>
          <AccountInfo subaccount={subaccount} accountSummary={accountSummary} accountNumber={accountNumber} />
          <Notes accountNumber={accountNumber} dispatch={dispatch} notes={notes} subnumber={subnumber} type="subaccount" />
          <section data-active={true} className={styles.collapseRef}>
            <div className={styles.titleSection}>
              <div className={styles.title}>
                <h5>Attachments</h5>
              </div>
              <span
                className={styles.leaveNoteIcon}
                onClick={() => openAttachmentModal(accountNumber, subnumber, dispatch, onSuccess)}
                title="Attach File"
              />
            </div>
            {showAttachments({ attachments, accountNumber, subnumber, dispatch, onSuccess })}
          </section>
        </div>
      </section>
    </div>
  )
}
